import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { format } from 'date-fns'
import { CheckCircle, Calendar, User, Settings, ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { useState, useEffect } from 'react'

export interface FeatureRequest {
	id: string
	title: string
	description: string
	status: string
	created_at: string
	submitter_name: string | null
	submitter_email: string | null
	submitter_client_user_id: string | null
	external_user_id: string | null
	user_properties: Record<string, any> | null
	is_publicly_visible: boolean
	public_response: string | null
	category_id?: string | null
	widget: { name: string } | null
	program: { name: string } | null
	upvote_count: number
	team_id?: string
	tags?: { id: string; name: string; color: string | null }[]
	internal_notes: string | null
}

export interface Tag {
	id: string
	name: string
	color: string | null
	program_id: string
}

interface FeatureDetailsTabProps {
	feature: FeatureRequest
	internalNotes: string
	onInternalNotesChange: (notes: string) => void
	isReadOnly?: boolean
}

export function FeatureDetailsTab({
	feature,
	internalNotes,
	onInternalNotesChange,
	isReadOnly = false,
}: FeatureDetailsTabProps) {
	const { t } = useTranslation()
	const [isInternalNotesExpanded, setIsInternalNotesExpanded] = useState(!!internalNotes)

	useEffect(() => {
		setIsInternalNotesExpanded(!!internalNotes)
	}, [feature.id])

	return (
		<>
			<div className="space-y-3">
				<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
					<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
						<svg className="h-2.5 w-2.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
						</svg>
					</div>
					{t('featureDetails.description')}
				</h3>
				<div className="bg-muted/50 p-4 rounded-lg min-h-[120px] whitespace-pre-wrap text-sm">
					{feature.description}
				</div>
			</div>

			<div className="space-y-3">
				<button
					type="button"
					onClick={() => setIsInternalNotesExpanded(!isInternalNotesExpanded)}
					className="text-sm font-medium text-muted-foreground flex items-center justify-between w-full hover:text-foreground transition-colors"
				>
					<div className="flex items-center gap-2">
						<div className="h-4 w-4 flex items-center justify-center bg-tertiary/80 rounded-full">
							<Settings className="h-2.5 w-2.5 text-primary" />
						</div>
						{t('featureDetails.internalNotes', 'Internal Notes')}
					</div>
					{isInternalNotesExpanded ? (
						<ChevronUp className="h-4 w-4" />
					) : (
						<ChevronDown className="h-4 w-4" />
					)}
				</button>
				{isInternalNotesExpanded && (
					<div className="space-y-2">
						<Textarea
							value={internalNotes}
							onChange={(e) => onInternalNotesChange(e.target.value)}
							disabled={isReadOnly}
							placeholder={t('featureDetails.addNotesPlaceholder', 'Type internal notes here...')}
							className="min-h-[100px] text-sm bg-muted/50 focus:bg-background transition-colors resize-none"
						/>
						<p className="text-[10px] text-muted-foreground italic px-1">
							{t('featureDetails.internalNotesDescription', 'Add private context or updates for your team. Not visible to users.')}
						</p>
					</div>
				)}
			</div>
		</>
	)
}

export function FeatureSubmitterBlock({ feature }: { feature: FeatureRequest }) {
	const { t } = useTranslation()
	return (
		<div className="space-y-3">
			<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
				<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
					<User className="h-2.5 w-2.5 text-primary" />
				</div>
				{t('featureDetails.submitter')}
			</h3>
			<div className="bg-muted/50 p-4 rounded-lg space-y-3">
				<div className="flex justify-between items-center">
					<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
						<div className="h-3 w-3 flex items-center justify-center">
							<User className="h-2 w-2 text-primary" />
						</div>
						{t('featureDetails.name')}:
					</span>
					<div className="flex items-center gap-2">
						<span className="text-xs text-foreground font-medium">
							{feature.submitter_name || t('featureDetails.anonymous')}
						</span>
						{feature.external_user_id && (
							<Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-xs">
								<CheckCircle className="h-3 w-3 mr-1" />
								{t('featureDetails.verified')}
							</Badge>
						)}
					</div>
				</div>

				{feature.submitter_email && (
					<div className="flex justify-between items-center">
						<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
							<div className="h-3 w-3 flex items-center justify-center">
								<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
								</svg>
							</div>
							{t('featureDetails.email')}:
						</span>
						<a
							href={`mailto:${feature.submitter_email}`}
							className="text-xs text-foreground font-medium hover:text-primary transition-colors"
						>
							{feature.submitter_email}
						</a>
					</div>
				)}

				<div className="flex justify-between items-center">
					<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
						<div className="h-3 w-3 flex items-center justify-center">
							<Calendar className="h-2 w-2 text-primary" />
						</div>
						{t('featureDetails.date')}:
					</span>
					<span className="text-xs text-foreground font-medium">
						{format(new Date(feature.created_at), 'MMM d, yyyy')} at {format(new Date(feature.created_at), 'h:mm a')}
					</span>
				</div>

				{feature.external_user_id && (
					<div className="flex justify-between items-start">
						<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
							<div className="h-3 w-3 flex items-center justify-center">
								<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
								</svg>
							</div>
							{t('featureDetails.userID')}:
						</span>
						<div className="flex-1 flex justify-end ml-2">
							<code className="text-foreground font-medium text-xs text-right">
								{feature.external_user_id}
							</code>
						</div>
					</div>
				)}

				{feature.user_properties && Object.entries(feature.user_properties).map(([key, value]) => (
					<div key={key} className="flex justify-between items-center">
						<span className="text-xs font-medium text-muted-foreground capitalize flex items-center gap-1">
							<div className="h-3 w-3 flex items-center justify-center">
								<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
							</div>
							{key.replace(/([A-Z])/g, ' $1')}:
						</span>
						<div className="flex-1 flex justify-end ml-2 text-xs">
							<span className="text-foreground font-medium text-right">{String(value)}</span>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}
